import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Save, User, Building2, Star, MessageSquare, Image } from 'lucide-react';
import PageHeader from '../../components/admin/PageHeader';
import ContentCard from '../../components/admin/ContentCard';
import LoadingState from '../../components/admin/LoadingState';
import toast from 'react-hot-toast';

const TestimonialForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = !!id;
  
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [testimonial, setTestimonial] = useState({
    author: '',
    position: '',
    company: '',
    content: '',
    image: '',
    rating: 5
  });

  useEffect(() => {
    document.title = isEditing ? 'Modifier un Témoignage | Admin' : 'Ajouter un Témoignage | Admin';
    
    if (isEditing) {
      loadTestimonial();
    }
  }, [isEditing, id]);

  const loadTestimonial = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('testimonials')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      
      if (data) {
        setTestimonial(data);
      }
    } catch (error) {
      console.error('Error loading testimonial:', error);
      toast.error('Erreur lors du chargement du témoignage');
      navigate('/admin/testimonials');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    if (name === 'rating') {
      setTestimonial({ ...testimonial, [name]: parseInt(value) });
    } else {
      setTestimonial({ ...testimonial, [name]: value });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      
      // Validate required fields
      if (!testimonial.author || !testimonial.content) {
        toast.error('Veuillez remplir tous les champs obligatoires');
        return;
      }
      
      let response;
      
      if (isEditing) {
        // Update existing testimonial
        response = await supabase
          .from('testimonials')
          .update({
            author: testimonial.author,
            position: testimonial.position,
            company: testimonial.company,
            content: testimonial.content,
            image: testimonial.image,
            rating: testimonial.rating
          })
          .eq('id', id);
      } else {
        // Insert new testimonial
        response = await supabase
          .from('testimonials')
          .insert([{
            author: testimonial.author,
            position: testimonial.position,
            company: testimonial.company,
            content: testimonial.content,
            image: testimonial.image,
            rating: testimonial.rating
          }]);
      }
      
      if (response.error) throw response.error;
      
      toast.success(isEditing ? 'Témoignage mis à jour avec succès' : 'Témoignage ajouté avec succès');
      navigate('/admin/testimonials');
    } catch (error) {
      console.error('Error saving testimonial:', error);
      toast.error('Erreur lors de l\'enregistrement du témoignage');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="px-6">
        <PageHeader
          title={isEditing ? 'Modifier le Témoignage' : 'Ajouter un Témoignage'}
          backLink="/admin/testimonials"
        />
        <LoadingState type="form" />
      </div>
    );
  }

  return (
    <div className="px-6">
      <PageHeader
        title={isEditing ? 'Modifier le Témoignage' : 'Ajouter un Témoignage'}
        backLink="/admin/testimonials"
      />
      
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <div className="lg:col-span-2">
            <ContentCard title="Informations du Témoignage">
              <div className="space-y-6">
                <div>
                  <label htmlFor="author" className="block text-sm font-medium text-gray-700 mb-1">
                    Auteur *
                  </label>
                  <div className="relative">
                    <User size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      id="author"
                      name="author"
                      value={testimonial.author}
                      onChange={handleChange}
                      className="pl-10 pr-3 py-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      required
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="position" className="block text-sm font-medium text-gray-700 mb-1">
                      Poste
                    </label>
                    <input
                      type="text"
                      id="position"
                      name="position"
                      value={testimonial.position}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                      Entreprise
                    </label>
                    <div className="relative">
                      <Building2 size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={testimonial.company}
                        onChange={handleChange}
                        className="pl-10 pr-3 py-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </div>
                </div>
                
                <div>
                  <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-1">
                    Témoignage *
                  </label>
                  <div className="relative">
                    <MessageSquare size={18} className="absolute left-3 top-3 text-gray-400" />
                    <textarea
                      id="content"
                      name="content"
                      value={testimonial.content}
                      onChange={handleChange}
                      rows={5}
                      className="pl-10 pr-3 py-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      required
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="rating" className="block text-sm font-medium text-gray-700 mb-1">
                      Note (1-5)
                    </label>
                    <div className="relative">
                      <Star size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <select
                        id="rating"
                        name="rating"
                        value={testimonial.rating}
                        onChange={handleChange}
                        className="pl-10 pr-3 py-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value={1}>1 étoile</option>
                        <option value={2}>2 étoiles</option>
                        <option value={3}>3 étoiles</option>
                        <option value={4}>4 étoiles</option>
                        <option value={5}>5 étoiles</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="image" className="block text-sm font-medium text-gray-700 mb-1">
                      Photo (URL)
                    </label>
                    <div className="relative">
                      <Image size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="url"
                        id="image"
                        name="image"
                        value={testimonial.image}
                        onChange={handleChange}
                        className="pl-10 pr-3 py-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="https://example.com/photo.jpg"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </ContentCard>
          </div>
          
          <div>
            <ContentCard title="Aperçu">
              <div className="space-y-6">
                <div className="flex items-center">
                  {testimonial.image ? (
                    <img
                      src={testimonial.image}
                      alt={testimonial.author}
                      className="w-16 h-16 rounded-full object-cover mr-4"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'https://via.placeholder.com/64?text=Photo';
                      }}
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center mr-4">
                      <User size={24} className="text-gray-400" />
                    </div>
                  )}
                  <div>
                    <h3 className="font-medium text-gray-900">{testimonial.author || 'Nom de l\'auteur'}</h3>
                    <p className="text-sm text-gray-500">
                      {testimonial.position && testimonial.company 
                        ? `${testimonial.position}, ${testimonial.company}`
                        : testimonial.position || testimonial.company || 'Poste, Entreprise'}
                    </p>
                  </div>
                </div>
                
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star 
                      key={star} 
                      size={16} 
                      className={star <= testimonial.rating ? "text-yellow-400 fill-current" : "text-gray-300"} 
                    />
                  ))}
                </div>
                
                <div className="p-4 bg-gray-50 rounded-md">
                  <p className="text-gray-700 italic">
                    "{testimonial.content || 'Contenu du témoignage...'}"
                  </p>
                </div>
              </div>
            </ContentCard>
          </div>
        </div>
        
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/admin/testimonials')}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center"
          >
            {saving ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Enregistrement...
              </>
            ) : (
              <>
                <Save size={16} className="mr-2" />
                {isEditing ? 'Mettre à jour' : 'Enregistrer'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TestimonialForm;